import { useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * <MultiSelect options={[{value, label, count?}]} value={[…]} onChange={fn} />
 *
 * Self-contained popover with a search box and checkbox list. Designed for
 * 5-200 options. For >200 options consider switching to a virtualized list.
 */
export function MultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  testid,
  width = "w-[220px]",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = q
    ? options.filter((o) => (o.label || "").toLowerCase().includes(q.toLowerCase()))
    : options;

  const toggle = (v) => {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          className={`${width} h-9 justify-between text-xs font-medium text-slate-700 hover:bg-slate-50`}
        >
          <span className="truncate">
            <span className="text-slate-500">{label}</span>
            {value.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-slate-900 text-white text-[10px] h-4 min-w-[16px] px-1 ml-1.5">
                {value.length}
              </span>
            )}
            {value.length === 0 && <span className="ml-1 text-slate-400">{placeholder}</span>}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-72">
        <div className="p-2 border-b border-slate-200">
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search options…"
            className="h-8 text-xs"
            data-testid={testid ? `${testid}-search` : undefined}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1" data-testid={testid ? `${testid}-list` : undefined}>
          {filtered.map((o) => {
            const selected = value.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-slate-50 ${
                  selected ? "bg-slate-50" : ""
                }`}
              >
                <span
                  className={`inline-flex h-4 w-4 items-center justify-center rounded border ${
                    selected ? "bg-slate-900 border-slate-900" : "border-slate-300"
                  }`}
                >
                  {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
                <span className="flex-1 truncate text-slate-800">{o.label}</span>
                {typeof o.count === "number" && (
                  <span className="text-[10px] text-slate-400 tabular-nums">{o.count}</span>
                )}
              </button>
            );
          })}
          {!filtered.length && (
            <div className="px-3 py-4 text-xs text-slate-400 text-center">No matches</div>
          )}
        </div>
        {value.length > 0 && (
          <div className="border-t border-slate-200 p-2 flex items-center justify-between">
            <span className="text-[10px] text-slate-500">{value.length} selected</span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X className="h-3 w-3" /> Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
